// Relecture d'un mémoire technique : section par section, bloc par bloc, chaque bloc avec sa source.
// Les données factuelles (postes du devis, fiche entreprise) sont verrouillées ; un trou « à compléter » se remplit sur place.
// Enregistrement automatique (contrôlé par le serveur : aucun prix, aucun montant, jamais « KNX conforme NF C 15-100 »).
// Relecture engagée (case nominative et horodatée), puis émission : numéro MEM-AAAA-NNNN, document figé.
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, CopyPlus, FileDown, FileType2, Lock, Printer, RefreshCw, Sparkles, Stamp } from 'lucide-react';
import { useSession } from '../../lib/session.tsx';
import { api, ErreurApi, messageErreur } from '../../lib/api.ts';
import { dateHeure, pluriel } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Chargement, Dialogue, EtatErreur, IndicateurEnregistrement, Panneau, useConfirmation, useNotifier, type EtatEnregistrement,
} from '../../components/ui.tsx';
import { DossierCadre } from '../etudes/DossierCadre.tsx';
import { LIBELLES_FORMAT, LIBELLES_SOURCE, STATUTS, type Bloc, type ContenuMemoire, type SectionMemoire, type VueMemoire } from './commun.ts';

export function PageMemoire() {
  const { id = '', mid = '' } = useParams();
  const q = useQuery({ queryKey: ['memoire', mid], queryFn: () => api.get<VueMemoire>(`/api/memoires/${mid}`) });
  if (q.isLoading) return <DossierCadre dossierId={id} actif="memoire" large><Chargement lignes={6} /></DossierCadre>;
  if (q.isError || !q.data) return <DossierCadre dossierId={id} actif="memoire" large><EtatErreur erreur={q.error} reessayer={() => q.refetch()} /></DossierCadre>;
  return <Relecture key={`${q.data.id}-${q.data.statut}`} dossierId={id} vue={q.data} />;
}

const MOTIFS_IA: Record<string, string> = {
  NOMBRE_INVENTE: 'elle ajoutait un nombre', NORME_INVENTEE: 'elle citait une norme absente', MARQUE_INVENTEE: 'elle citait une marque absente',
  KNX_NFC15100: 'elle rattachait le KNX à la NF C 15-100', MONTANT: 'elle contenait un montant', STRUCTURE: 'réponse hors format ou trop longue',
};

interface ErreurControle { code?: string; section?: string; bloc?: string; extrait?: string; message?: string }

function Relecture({ dossierId, vue: initiale }: { dossierId: string; vue: VueMemoire }) {
  const qc = useQueryClient();
  const naviguer = useNavigate();
  const notifier = useNotifier();
  const { confirmer, element: confirmation } = useConfirmation();
  const [vue, setVue] = useState(initiale);
  const [contenu, setContenu] = useState<ContenuMemoire>(initiale.contenu);
  const [etat, setEtat] = useState<EtatEnregistrement>('enregistre');
  const [quand, setQuand] = useState<string>();
  const [erreurs, setErreurs] = useState<ErreurControle[]>([]);
  const [action, setAction] = useState<string | null>(null);
  const [engagement, setEngagement] = useState(false);
  const [versionDocument, setVersionDocument] = useState<'travail' | 'definitive'>(initiale.manques.length ? 'travail' : 'definitive');
  const minuterie = useRef<ReturnType<typeof setTimeout>>(undefined);
  const enAttente = useRef<ContenuMemoire | null>(null);
  const versionRef = useRef(initiale.version);
  const modifiable = vue.statut !== 'emis';
  const { etat: services } = useSession();
  const iaDisponible = !!services?.ia?.disponible && vue.format !== 'note';
  const [proposition, setProposition] = useState<{ section: SectionMemoire; blocs: { id: string; texte?: string; items?: string[] }[] } | null>(null);

  const appliquer = (v: VueMemoire) => { setVue(v); versionRef.current = v.version; qc.setQueryData(['memoire', v.id], v); };
  const rafraichirListes = () => { qc.invalidateQueries({ queryKey: ['memoires', dossierId] }); qc.invalidateQueries({ queryKey: ['dossier', dossierId] }); };

  const vider = async () => {
    clearTimeout(minuterie.current);
    const c = enAttente.current;
    if (!c) return true;
    enAttente.current = null;
    setEtat('en_cours');
    try {
      const v = await api.patch<VueMemoire>(`/api/memoires/${vue.id}`, { contenu: c, version: versionRef.current });
      appliquer(v);
      // « Modifié par vous » est calculé par le serveur (empreinte d'origine) : on reporte ce drapeau sans toucher au texte en cours de frappe.
      const drapeaux = new Map(v.contenu.sections.flatMap((s) => s.blocs.map((b) => [b.id, b.modifie] as const)));
      setContenu((x) => ({ ...x, sections: x.sections.map((s) => ({ ...s, blocs: s.blocs.map((b) => (drapeaux.has(b.id) ? { ...b, modifie: drapeaux.get(b.id)! } : b)) })) }));
      setErreurs([]);
      setEtat('enregistre'); setQuand(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
      if (v.statut !== vue.statut) setEngagement(false);
      return true;
    } catch (e) {
      setEtat('erreur');
      if (e instanceof ErreurApi && Array.isArray(e.details)) setErreurs(e.details as ErreurControle[]);
      notifier(messageErreur(e), true);
      return false;
    }
  };
  useEffect(() => () => { void vider(); }, []);

  const modifier = (fn: (c: ContenuMemoire) => void) => {
    if (!modifiable) return;
    setContenu((avant) => {
      const c = structuredClone(avant);
      fn(c);
      enAttente.current = c;
      return c;
    });
    setEtat('modifie');
    clearTimeout(minuterie.current);
    minuterie.current = setTimeout(() => { void vider(); }, 1000);
  };
  const changerBloc = (sid: string, bid: string, maj: Partial<Bloc>) => modifier((c) => {
    const b = c.sections.find((s) => s.id === sid)?.blocs.find((x) => x.id === bid);
    if (b) Object.assign(b, maj);
  });
  const basculerSection = (sid: string, incluse: boolean) => modifier((c) => {
    const s = c.sections.find((x) => x.id === sid);
    if (s) { s.incluse = incluse; if (incluse) s.raison_exclusion = null; }
  });

  const executer = async (nom: string, fn: () => Promise<void>) => {
    setAction(nom);
    try { if (await vider()) await fn(); } catch (e) {
      if (e instanceof ErreurApi && Array.isArray(e.details)) setErreurs(e.details as ErreurControle[]);
      notifier(messageErreur(e), true);
    } finally { setAction(null); }
  };

  const reformuler = (sec: SectionMemoire) => executer(`ia-${sec.id}`, async () => {
    const r = await api.post<{ proposition: { blocs: { id: string; texte?: string; items?: string[] }[] } | null; violations: { code: string; extrait: string }[]; reformulations_restantes: number }>(
      `/api/memoires/${vue.id}/sections/${sec.id}/reformuler`, {});
    if (!r.proposition) {
      const motifs = [...new Set(r.violations.map((v) => MOTIFS_IA[v.code] ?? v.code))].join(' ; ');
      notifier(`Proposition de l’IA écartée : ${motifs}. Votre texte reste tel quel.`, true);
      return;
    }
    setProposition({ section: sec, blocs: r.proposition.blocs });
  });
  const accepter = () => {
    if (!proposition) return;
    modifier((c) => {
      const sec = c.sections.find((x) => x.id === proposition.section.id);
      for (const p of proposition.blocs) {
        const b = sec?.blocs.find((x) => x.id === p.id);
        if (b) Object.assign(b, p.items ? { items: p.items } : { texte: p.texte }, { origine: 'ia' });
      }
    });
    setProposition(null);
    notifier('Proposition acceptée : relisez-la. Le document portera la mention « Texte généré par IA, relu par l’entreprise ».');
  };

  const relire = () => executer('relire', async () => {
    const v = await api.post<VueMemoire>(`/api/memoires/${vue.id}/relu`, { engagement, version: versionRef.current });
    appliquer(v); rafraichirListes(); notifier('Relecture enregistrée. Vous pouvez émettre le mémoire.');
  });
  const emettre = () => executer('emettre', async () => {
    const ok = await confirmer('Émettre le mémoire ?',
      versionDocument === 'definitive'
        ? 'Le mémoire reçoit un numéro et devient définitif : il ne pourra plus être modifié. Pour le corriger, vous créerez une nouvelle version.'
        : 'Le mémoire reçoit un numéro et devient figé, marqué « Version de travail » : les informations manquantes y restent signalées.',
      'Émettre le mémoire');
    if (!ok) return;
    const r = await api.post<{ numero: string; memoire: VueMemoire }>(`/api/memoires/${vue.id}/emettre`, { version_document: versionDocument, version: versionRef.current });
    appliquer(r.memoire); setContenu(r.memoire.contenu); rafraichirListes();
    notifier(`Mémoire ${r.numero} émis. Téléchargez le PDF ou le Word.`);
  });
  const regenerer = () => executer('regenerer', async () => {
    const ok = await confirmer('Repartir des données du dossier ?',
      'Le mémoire est reconstruit avec le devis, les exigences et la fiche entreprise actuels. Vos modifications sont gardées quand le texte d’origine n’a pas changé ; les autres sont perdues. La relecture est à refaire.',
      'Mettre à jour');
    if (!ok) return;
    const r = await api.post<{ repris: number; perdus: number; memoire: VueMemoire }>(`/api/memoires/${vue.id}/regenerer`, { version: versionRef.current });
    appliquer(r.memoire); setContenu(r.memoire.contenu); setEngagement(false);
    notifier(`Mémoire mis à jour : ${pluriel(r.repris, 'modification gardée', 'modifications gardées')}${r.perdus ? `, ${pluriel(r.perdus, 'perdue (texte d’origine changé)', 'perdues (texte d’origine changé)')}` : ''}.`);
  });
  const nouvelleVersion = () => executer('version', async () => {
    const r = await api.post<{ id: string }>(`/api/memoires/${vue.id}/nouvelle-version`, {});
    rafraichirListes(); notifier('Nouvelle version créée en brouillon.');
    naviguer(`/etudes/${dossierId}/memoires/${r.id}`);
  });
  const supprimer = () => executer('supprimer', async () => {
    const ok = await confirmer('Supprimer ce brouillon ?', 'Le brouillon et vos modifications seront supprimés. Le dossier n’est pas modifié.', 'Supprimer le brouillon');
    if (!ok) return;
    await api.del(`/api/memoires/${vue.id}`);
    rafraichirListes(); notifier('Brouillon supprimé.');
    naviguer(`/etudes/${dossierId}/memoire`);
  });

  const pagesMax = Number(vue.saisie?.pages_max) || null;
  const titre = vue.numero ? `Mémoire ${vue.numero}` : 'Mémoire en préparation';
  const actions = (
    <>
      <a className="btn btn-principal" href={`/api/memoires/${vue.id}/memoire.pdf`} target="_blank" rel="noopener"><FileDown aria-hidden />PDF</a>
      <a className="btn" href={`/api/memoires/${vue.id}/memoire.docx`}><FileType2 aria-hidden />Word</a>
      <a className="btn" href={`/api/memoires/${vue.id}/memoire.html`} target="_blank" rel="noopener"><Printer aria-hidden />Version imprimable</a>
    </>
  );

  return (
    <DossierCadre dossierId={dossierId} actif="memoire" large actions={actions}>
      <div className="me-page">
        <aside className="me-cote pile" aria-label="État du mémoire">
          <Panneau titre={titre} actions={modifiable ? <IndicateurEnregistrement etat={etat} quand={quand} /> : undefined}>
            <div className="pile" style={{ gap: 10 }}>
              <div className="rangee" style={{ gap: 6, flexWrap: 'wrap' }}>
                <Badge ton={STATUTS[vue.statut].ton}>{STATUTS[vue.statut].libelle}</Badge>
                <Badge ton="neutre" icone={false}>{LIBELLES_FORMAT[vue.format]}</Badge>
                {vue.version_no > 1 && <Badge ton="neutre" icone={false}>Version {vue.version_no}</Badge>}
                {vue.version_document === 'travail' && <Badge ton="ambre" icone={false}>Version de travail</Badge>}
              </div>
              <p className="petit">Environ <strong>{pluriel(vue.pages_estimees, 'page', 'pages')}</strong>{pagesMax ? ` — limite ${pagesMax}` : ''}.</p>
              {pagesMax && vue.pages_estimees > pagesMax && <Alerte ton="danger">Le mémoire dépasse la limite de pages : un dépassement peut rendre l’offre irrégulière. Retirez des sections ou passez en note méthodologique.</Alerte>}
              {vue.relu && <p className="petit secondaire"><CheckCircle2 size={14} aria-hidden /> Relu par {vue.relu.par} le {dateHeure(vue.relu.le)}.</p>}
              {vue.emis && <p className="petit secondaire">Émis par {vue.emis.par} le {dateHeure(vue.emis.le)}. Il ne peut plus être modifié.</p>}
              {vue.precedent && <p className="petit">Remplace : <Link to={`/etudes/${dossierId}/memoires/${vue.precedent.id}`}>mémoire {vue.precedent.numero ?? 'précédent'}</Link></p>}
              {vue.suivant && <p className="petit">Remplacé par : <Link to={`/etudes/${dossierId}/memoires/${vue.suivant.id}`}>{vue.suivant.numero ? `mémoire ${vue.suivant.numero}` : 'une nouvelle version en préparation'}</Link></p>}

              {vue.manques.length > 0 && (
                <details className="me-manques" open={vue.statut !== 'emis' && vue.manques.length <= 8}>
                  <summary className="petit"><strong>{pluriel(vue.manques.length, 'information à compléter', 'informations à compléter')}</strong></summary>
                  <ul className="et-liste-simple petit">{vue.manques.slice(0, 40).map((x, i) => <li key={i}><span className="secondaire">{x.section} :</span> {x.texte}</li>)}</ul>
                </details>
              )}

              {modifiable && (
                <div className="groupe-btn">
                  <Bouton compact icone={<RefreshCw aria-hidden />} chargement={action === 'regenerer'} onClick={regenerer}>Mettre à jour depuis le dossier</Bouton>
                  <Bouton compact variante="fantome" chargement={action === 'supprimer'} onClick={supprimer}>Supprimer le brouillon</Bouton>
                </div>
              )}

              {vue.statut === 'brouillon' && (
                <div className="me-emission pile" style={{ gap: 8 }}>
                  <label className="case me-engagement"><input type="checkbox" checked={engagement} onChange={(e) => setEngagement(e.target.checked)} /><span className="petit">{vue.engagement}</span></label>
                  <Bouton variante="principal" icone={<CheckCircle2 aria-hidden />} disabled={!engagement} chargement={action === 'relire'} onClick={relire}>Enregistrer ma relecture</Bouton>
                </div>
              )}
              {vue.statut === 'relu' && (
                <div className="me-emission pile" style={{ gap: 8 }}>
                  <fieldset className="me-versions">
                    <legend className="petit"><strong>Version à émettre</strong></legend>
                    <label className="case"><input type="radio" name="version" checked={versionDocument === 'definitive'} disabled={vue.manques.length > 0} onChange={() => setVersionDocument('definitive')} />
                      <span className="petit">Définitive{vue.manques.length > 0 ? ' — impossible tant que des informations manquent' : ''}</span></label>
                    <label className="case"><input type="radio" name="version" checked={versionDocument === 'travail'} onChange={() => setVersionDocument('travail')} />
                      <span className="petit">Version de travail (filigranée, trous signalés)</span></label>
                  </fieldset>
                  <Bouton variante="principal" icone={<Stamp aria-hidden />} chargement={action === 'emettre'} onClick={emettre}>Émettre le mémoire…</Bouton>
                </div>
              )}
              {vue.statut === 'emis' && !vue.suivant && (
                <Bouton icone={<CopyPlus aria-hidden />} chargement={action === 'version'} onClick={nouvelleVersion}>Nouvelle version</Bouton>
              )}
            </div>
          </Panneau>
        </aside>

        <div className="me-principal pile-l">
          {erreurs.length > 0 && (
            <Alerte ton="danger" titre="Modification refusée">
              <ul className="et-liste-simple">{erreurs.map((e, i) => <li key={i}>{e.message ?? `${e.code ?? ''}${e.extrait ? ` : « ${e.extrait} »` : ''}`}</li>)}</ul>
            </Alerte>
          )}
          {vue.demo && <Alerte ton="ambre">Dossier de démonstration : données fictives, document sans valeur contractuelle.</Alerte>}
          {modifiable && (
            <Alerte ton="info" titre="Ce document engage l’entreprise">
              Les moyens, méthodes et délais écrits ici deviennent des engagements. Relisez chaque section ; les blocs marqués d’un cadenas reprennent le devis ou la fiche entreprise et se corrigent à la source.
            </Alerte>
          )}
          {contenu.sections.map((s) => (
            <SectionRelecture key={s.id} s={s} modifiable={modifiable} changerBloc={changerBloc} basculer={basculerSection} erreurs={erreurs}
              reformuler={modifiable && iaDisponible && s.reformulable && s.statut_ia !== 'non' && s.incluse ? () => reformuler(s) : undefined} enCours={action === `ia-${s.id}`} />
          ))}
        </div>
      </div>
      {confirmation}
      <Dialogue titre={proposition ? `Proposition de l’IA — ${proposition.section.titre}` : ''} ouvert={!!proposition} fermer={() => setProposition(null)} large
        pied={<><Bouton onClick={() => setProposition(null)}>Garder mon texte</Bouton><Bouton variante="principal" onClick={accepter}>Accepter la proposition</Bouton></>}>
        {proposition && (
          <div className="pile">
            <p className="petit secondaire">Forme seulement : la proposition a été vérifiée (aucun nombre, aucune norme, aucune marque ajoutés). Relisez-la : elle engagera l’entreprise comme le reste du mémoire.</p>
            {proposition.blocs.map((p) => {
              const b = proposition.section.blocs.find((x) => x.id === p.id);
              const avant = b ? (b.type === 'liste' ? b.items.join('\n') : b.texte) : '';
              const apres = p.items ? p.items.join('\n') : p.texte ?? '';
              return (
                <div key={p.id} className="me-comparaison">
                  <div><span className="tres-petit discret">Votre texte</span><p className="petit me-pre">{avant}</p></div>
                  <div><span className="tres-petit discret">Proposition de l’IA</span><p className="petit me-pre">{apres}</p></div>
                </div>
              );
            })}
          </div>
        )}
      </Dialogue>
    </DossierCadre>
  );
}

function SectionRelecture({ s, modifiable, changerBloc, basculer, erreurs, reformuler, enCours }:
  { s: SectionMemoire; modifiable: boolean; changerBloc: (sid: string, bid: string, maj: Partial<Bloc>) => void; basculer: (sid: string, v: boolean) => void; erreurs: ErreurControle[];
    reformuler?: () => void; enCours?: boolean }) {
  const trous = s.blocs.filter((b) => b.type === 'a_completer').length;
  return (
    <Panneau id={`sec-${s.id}`} className={s.incluse ? '' : 'me-section-exclue'} titre={`${s.numero}. ${s.titre}`}
      actions={<label className="case me-case-section"><input type="checkbox" checked={s.incluse} disabled={!modifiable || !!s.erreur} onChange={(e) => basculer(s.id, e.target.checked)} /><span className="petit">Inclure</span></label>}>
      <div className="pile">
        <div className="rangee" style={{ gap: 6, flexWrap: 'wrap' }}>
          {s.complete === 'complete' && <Badge ton="succes" icone={false}>Complète</Badge>}
          {trous > 0 && <Badge ton="ambre" icone={false}>{pluriel(trous, 'trou à compléter', 'trous à compléter')}</Badge>}
          {s.statut_ia === 'non' && <Badge ton="neutre" icone={false}>Données factuelles</Badge>}
          {reformuler && <Bouton compact variante="fantome" icone={<Sparkles aria-hidden />} chargement={enCours} onClick={reformuler}>Proposer une reformulation (IA)</Bouton>}
        </div>
        {!s.incluse && <p className="secondaire petit">{s.raison_exclusion ?? 'Section non incluse dans le document.'}</p>}
        {s.incluse && s.blocs.map((b) => (
          <BlocRelecture key={b.id} b={b} modifiable={modifiable} changer={(maj) => changerBloc(s.id, b.id, maj)} enErreur={erreurs.some((e) => e.bloc === b.id)} />
        ))}
      </div>
    </Panneau>
  );
}

function ZoneTexte({ valeur, changer, libelle, lignes = 3 }: { valeur: string; changer: (v: string) => void; libelle: string; lignes?: number }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { const el = ref.current; if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight + 2}px`; } }, [valeur]);
  return <textarea ref={ref} aria-label={libelle} className="saisie me-texte" rows={lignes} value={valeur} maxLength={4000} onChange={(e) => changer(e.target.value)} />;
}

function Sources({ b }: { b: Bloc }) {
  if (!b.sources.length) return null;
  return (
    <details className="me-sources">
      <summary className="tres-petit discret">{pluriel(b.sources.length, 'source', 'sources')}</summary>
      <ul className="tres-petit">{b.sources.map((x, i) => <li key={i}><strong>{LIBELLES_SOURCE[x.type] ?? x.type}</strong> — {x.libelle}{x.ref ? ` (${x.ref})` : ''}</li>)}</ul>
    </details>
  );
}

function BlocRelecture({ b, modifiable, changer, enErreur }: { b: Bloc; modifiable: boolean; changer: (maj: Partial<Bloc>) => void; enErreur: boolean }) {
  const editable = modifiable && !b.verrouille;
  const marques = (
    <span className="rangee me-marques" style={{ gap: 6, flexWrap: 'wrap' }}>
      {b.verrouille && <Badge ton="neutre" icone={<Lock size={12} aria-hidden />}>{b.origine === 'fiche' ? 'Repris de la fiche entreprise' : b.origine === 'devis' ? 'Repris du devis' : 'Repris du dossier'}</Badge>}
      {b.modifie && <Badge ton="info" icone={false}>Modifié par vous</Badge>}
      {b.origine === 'ia' && <Badge ton="ambre">Reformulé par IA — à relire</Badge>}
      {b.interne && <Badge ton="neutre" icone={false}>Interne — non imprimé</Badge>}
    </span>
  );
  const cadre = (corps: React.ReactNode) => (
    <div className={`me-bloc ${enErreur ? 'me-bloc-erreur' : ''} ${b.interne ? 'me-bloc-interne' : ''}`}>{corps}{(b.verrouille || b.modifie || b.origine === 'ia' || b.interne) && marques}<Sources b={b} /></div>
  );
  switch (b.type) {
    case 'titre': return b.niveau === 3 ? <h4 className="me-titre3">{b.texte}</h4> : <h3 className="me-titre2">{b.texte}</h3>;
    case 'paragraphe': case 'avertissement':
      return cadre(editable ? <ZoneTexte libelle="Paragraphe" valeur={b.texte} changer={(v) => changer({ texte: v })} /> : <p className={b.type === 'avertissement' ? 'me-avert' : ''}>{b.texte}</p>);
    case 'liste':
      return cadre(editable
        ? <ZoneTexte libelle="Liste (un élément par ligne)" lignes={Math.max(2, b.items.length)} valeur={b.items.join('\n')} changer={(v) => changer({ items: v.split('\n') })} />
        : <ul className="me-ul">{b.items.map((x, i) => <li key={i}>{x}</li>)}</ul>);
    case 'tableau':
      return cadre(
        <div className="me-tab" role="region" aria-label="Tableau" tabIndex={0}>
          <table><thead><tr>{b.colonnes.map((c, i) => <th key={i} scope="col">{c}</th>)}</tr></thead>
            <tbody>{b.lignes.map((l, i) => <tr key={i}>{l.map((c, k) => <td key={k}>{c}</td>)}</tr>)}</tbody></table>
        </div>);
    case 'a_completer':
      return (
        <div className={`me-trou ${enErreur ? 'me-bloc-erreur' : ''}`}>
          <p className="petit"><strong>À compléter :</strong> {b.texte}</p>
          {modifiable && (
            <ZoneTexte libelle={`Compléter : ${b.texte}`} valeur="" lignes={2}
              changer={(v) => { if (v.trim()) changer({ type: 'paragraphe', texte: v, origine: 'utilisateur' }); }} />
          )}
          <Sources b={b} />
        </div>
      );
    default: return b.texte ? cadre(<p className="petit secondaire">{b.texte}</p>) : null;
  }
}
